'use client';

import React, { useState, useEffect, useRef } from 'react';
import { getConsent, saveConsent, logConsentAction } from '@/lib/consent';

type ConsentRegion = 'gdpr' | 'ccpa' | 'none';

interface ConsentPreferenceCenterProps {
  region: ConsentRegion;
  onClose: () => void;
}

export default function ConsentPreferenceCenter({ region, onClose }: ConsentPreferenceCenterProps) {
  const [choices, setChoices] = useState({ necessary: true, analytics: false, marketing: false, preferences: false });
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLButtonElement>(null);
  const lastFocusableRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const currentConsent = getConsent();
    if (currentConsent) setChoices(currentConsent.choices);
    firstFocusableRef.current?.focus();

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const focusableElements = dialogRef.current?.querySelectorAll('button, [role="switch"]') as NodeListOf<HTMLElement>;
      if (!focusableElements || focusableElements.length === 0) return;
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement as HTMLElement;
      if (e.shiftKey) {
        if (activeElement === firstElement) { e.preventDefault(); lastElement.focus(); }
      } else {
        if (activeElement === lastElement) { e.preventDefault(); firstElement.focus(); }
      }
    };

    document.addEventListener('keydown', handleEscape);
    dialogRef.current?.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleEscape);
      dialogRef.current?.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const handleToggle = (category: keyof typeof choices) => {
    if (category === 'necessary') return;
    setChoices(prev => ({ ...prev, [category]: !prev[category] }));
  };

  const handleSave = () => {
    saveConsent(region, choices);
    logConsentAction('preferences_saved', { choices, region });
    onClose();
  };

  const handleAcceptAll = () => {
    const allAccepted = { necessary: true, analytics: true, marketing: true, preferences: true };
    setChoices(allAccepted);
    saveConsent(region, allAccepted);
    logConsentAction('accept_all_from_preferences', { region });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }} onClick={onClose}>
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="preferences-title" className="rounded-xl border max-w-md w-full p-6 max-h-[90vh] overflow-y-auto" style={{ backgroundColor: 'rgb(8, 11, 18)', borderColor: 'rgba(31, 34, 39, 0.5)' }} onClick={e => e.stopPropagation()}>
        <h2 id="preferences-title" className="font-display font-bold text-xl mb-4" style={{ color: 'oklch(95% .005 265)' }}>Cookie Preferences</h2>
        <div className="space-y-4 mb-6">
          <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(31, 34, 39, 0.2)' }}>
            <div className="flex items-center justify-between mb-2">
              <label className="font-semibold text-sm" style={{ color: 'oklch(95% .005 265)' }}>Necessary</label>
              <button role="switch" aria-checked={choices.necessary} disabled className="w-10 h-6 rounded-full transition-colors cursor-not-allowed" style={{ backgroundColor: 'rgb(43, 98, 239)' }} />
            </div>
            <p className="text-xs" style={{ color: 'oklch(55% .01 265)' }}>Required for site functionality, security, and authentication. Always enabled.</p>
          </div>
          <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(31, 34, 39, 0.2)' }}>
            <div className="flex items-center justify-between mb-2">
              <label className="font-semibold text-sm" style={{ color: 'oklch(95% .005 265)' }}>Analytics</label>
              <button ref={firstFocusableRef} role="switch" aria-checked={choices.analytics} onClick={() => handleToggle('analytics')} className="w-10 h-6 rounded-full transition-colors" style={{ backgroundColor: choices.analytics ? 'rgb(43, 98, 239)' : 'rgba(31, 34, 39, 0.5)' }} />
            </div>
            <p className="text-xs" style={{ color: 'oklch(55% .01 265)' }}>Help us understand how you use our site to improve your experience.</p>
          </div>
          <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(31, 34, 39, 0.2)' }}>
            <div className="flex items-center justify-between mb-2">
              <label className="font-semibold text-sm" style={{ color: 'oklch(95% .005 265)' }}>Marketing</label>
              <button role="switch" aria-checked={choices.marketing} onClick={() => handleToggle('marketing')} className="w-10 h-6 rounded-full transition-colors" style={{ backgroundColor: choices.marketing ? 'rgb(43, 98, 239)' : 'rgba(31, 34, 39, 0.5)' }} />
            </div>
            <p className="text-xs" style={{ color: 'oklch(55% .01 265)' }}>Enable personalized ads and marketing content based on your interests.</p>
          </div>
          <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(31, 34, 39, 0.2)' }}>
            <div className="flex items-center justify-between mb-2">
              <label className="font-semibold text-sm" style={{ color: 'oklch(95% .005 265)' }}>Preferences</label>
              <button role="switch" aria-checked={choices.preferences} onClick={() => handleToggle('preferences')} className="w-10 h-6 rounded-full transition-colors" style={{ backgroundColor: choices.preferences ? 'rgb(43, 98, 239)' : 'rgba(31, 34, 39, 0.5)' }} />
            </div>
            <p className="text-xs" style={{ color: 'oklch(55% .01 265)' }}>Remember your preferences and personalize your experience.</p>
          </div>
        </div>
        <div className="flex flex-col gap-3">
          <button ref={lastFocusableRef} onClick={handleAcceptAll} className="w-full px-4 py-2.5 rounded-lg text-white font-semibold transition-all" style={{ backgroundColor: 'rgb(43, 98, 239)' }}>Accept All</button>
          <button onClick={handleSave} className="w-full px-4 py-2.5 rounded-lg border font-semibold transition-all" style={{ borderColor: 'rgba(31, 34, 39, 0.5)', color: 'oklch(95% .005 265)' }}>Save Preferences</button>
        </div>
      </div>
    </div>
  );
}
